import { useState, useCallback, useEffect, useRef, type HTMLAttributes, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Button, IconButton, Link, Modal, ModalContent } from '@restworld/ui-ds-base';
import { useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { cn, useAnalytics, useResponsiveBreakPoints } from '@restworld/utils-common';

import { referralInviteModalAtom } from './referral-modal.atoms';

const STEPS = [
  { emoji: '🔗', titleKey: 'referral_invite_modal_step_1_title', descKey: 'referral_invite_modal_step_1_desc' },
  { emoji: '🙌', titleKey: 'referral_invite_modal_step_2_title', descKey: 'referral_invite_modal_step_2_desc' },
  { emoji: '🎉', titleKey: 'referral_invite_modal_step_3_title', descKey: 'referral_invite_modal_step_3_desc' },
];

const REFERRAL_PAGE_PATH = '/employer/referral';

export function ReferralInviteModal() {
  const { t } = useTranslation('common');
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const employersService = useEmployersService();
  const [modalState, setModalState] = useAtom(referralInviteModalAtom);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // On mobile the Modal renders as a bottom-sheet that closes via swipe gesture, so the X is desktop-only.
  // isMobile (<768) mirrors the Modal's mobile/desktop switch, so the X shows exactly when the desktop dialog does.
  const { isMobile } = useResponsiveBreakPoints();

  const { isOpen, source } = modalState;

  const { data } = useQuery({
    queryKey: ['referral-link'],
    queryFn: () => employersService.getReferralLink().then((r) => r.data),
    enabled: !!user?.using_ppu_business_model && isOpen,
  });

  const referralLink = data?.referral_link ?? '';

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      return;
    }
    if (!user?.using_ppu_business_model) return;
    trackEvent('referral_share_modal_opened', { source });
  }, [isOpen, source, trackEvent, user?.using_ppu_business_model]);

  const handleClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, [setModalState]);

  const handleCopy = useCallback(async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      const el = document.createElement('textarea');
      el.value = referralLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    trackEvent('referral_share_channel_clicked', { channel: 'copy_link', source });
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    setCopied(true);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  }, [referralLink, trackEvent, source]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!user?.using_ppu_business_model) return null;

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <ModalContent className="w-full max-w-[480px] !px-0" noPadding showCloseIcon={false}>
        <ContentWrapper>
          {!isMobile && (
            <ModalTopRow>
              <IconButton
                Icon={XMarkIcon}
                ariaLabel={t('referral_invite_modal_close_aria_label')}
                variant="neutral"
                onClick={handleClose}
              />
            </ModalTopRow>
          )}

          <ModalInner>
            <Badge>{t('referral_invite_modal_badge')}</Badge>
            <ModalTitle>{t('referral_invite_modal_title')}</ModalTitle>

            <StepsList>
              {STEPS.map(({ emoji, titleKey, descKey }) => (
                <StepRow key={titleKey}>
                  <StepEmoji>{emoji}</StepEmoji>
                  <StepText>
                    <StepTitle>{t(titleKey)}</StepTitle>
                    <StepDesc>{t(descKey)}</StepDesc>
                  </StepText>
                </StepRow>
              ))}
            </StepsList>

            <LinkRow>
              <LinkInput readOnly value={referralLink} aria-label={t('referral_invite_modal_link_aria_label')} />
              <Button
                variant={copied ? 'secondary-success' : 'secondary-restworld'}
                onClick={handleCopy}
                disabled={!referralLink}
                StartIcon={copied ? CheckIcon : undefined}
              >
                {copied ? t('referral_invite_modal_link_copied') : t('referral_invite_modal_copy_link')}
              </Button>
            </LinkRow>

            <Divider />
            <FullPageRow>
              <FullPageText>{t('referral_invite_modal_full_page_text')}</FullPageText>
              <Link
                href={REFERRAL_PAGE_PATH}
                newTab={false}
                size="md"
                variant="restworld"
                EndIcon={ArrowRightIcon}
                onClick={handleClose}
              >
                {t('referral_invite_modal_full_page_link')}
              </Link>
            </FullPageRow>
          </ModalInner>
        </ContentWrapper>
      </ModalContent>
    </Modal>
  );
}

export default ReferralInviteModal;

/* --- Layout primitives (className-based; converted from styled-components per migration §1) --- */

const ContentWrapper = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col w-full', className)} {...props} />
);

const ModalTopRow = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex justify-end px-4 pt-4 pb-1', className)} {...props} />
);

const ModalInner = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-5 px-6 pb-6', className)} {...props} />
);

const Badge = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'inline-flex items-center self-start px-2 h-6 rounded bg-[#f0ebf8] text-[#482485] font-medium text-base leading-[1.21]',
      className
    )}
    {...props}
  />
);

const ModalTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn('font-semibold text-[#1c1c33] mt-0 mb-2 text-xl leading-[1.3]', className)} {...props} />
);

const StepsList = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-3', className)} {...props} />
);

const StepRow = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-start gap-3', className)} {...props} />
);

const StepEmoji = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-xl leading-[1.4] shrink-0 w-7 text-center', className)} {...props} />
);

const StepText = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-0.5', className)} {...props} />
);

const StepTitle = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('font-semibold text-[#1c1c33] text-base', className)} {...props} />
);

const StepDesc = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-base text-[#666676]', className)} {...props} />
);

const LinkRow = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2 my-2', className)} {...props} />
);

const LinkInput = ({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={cn(
      'flex-1 text-base text-[#666676] bg-[#f5f5f7] border border-[#dedee1] rounded-lg px-3 h-10 truncate outline-none',
      className
    )}
    {...props}
  />
);

const Divider = ({ className, ...props }: HTMLAttributes<HTMLHRElement>) => (
  <hr className={cn('w-full m-0 border-0 border-t border-t-[#dedee1]', className)} {...props} />
);

const FullPageRow = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-wrap items-center justify-center gap-1 self-center text-center', className)}
    {...props}
  />
);

const FullPageText = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('text-base text-[#666676]', className)} {...props} />
);
